import { Credit } from "@/models/Credit";
import { Cronograma } from "@/models/Cronograma";
import ToolsService from "@/services/ToolsService";
import { computed, inject, ref } from "vue";
import pdfMake from "pdfmake/build/pdfmake";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import { useAuthStore } from "@/store/auth.store";
import { ToastType } from "@/types/toast";
import Toast from "@/plugins/Toast";

export default class SchedulesDialogViewModel {
  public dialog = ref<boolean>(false);
  public schedules = ref<Cronograma[]>();
  public credit = ref<Credit>();
  public toast: Toast;
  public toolsService = new ToolsService();
  public loading_pdf = ref<boolean>(false); // Optional: You can manage loading states if needed
  public authStore = useAuthStore();
  public userinfo: Record<string, any> | null;
  public coin = computed(() =>
    this.toolsService.getCoin(this.credit.value?.moneda || 1)
  );

  constructor() {
    this.toast = inject("toast") as Toast;
    this.userinfo = this.authStore.getUserData;
  }
  public formatDate = (date: string) => {
    return this.toolsService.formatDate(date);
  };
  public isCanceled = (es_cancelado: number) => {
    return es_cancelado === 1 ? "Sí" : "No";
  };
  public getFloat = (_number: number) => {
    return this.toolsService.getFloat(_number);
  };

  public downloadSchedule = async () => {
    this.loading_pdf.value = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const logoPath = require("@/assets/images/logo.png");
      const { width: logoWidth, height: logoHeight } =
        await this.toolsService.getImageDimensions(logoPath);

      // Convertir la imagen a base64
      const logoBase64 = await this.toolsService.convertImageToBase64(logoPath);

      // Calcular el tamaño proporcional
      const { width, height } = this.toolsService.calculateImageSize(
        logoWidth,
        logoHeight,
        50
      );

      const docDefinition: TDocumentDefinitions = {
        content: [
          {
            image: logoBase64,
            width, // Ancho calculado proporcional
            height, // Altura fija
            alignment: "center",
            margin: [0, 0, 0, 5],
          },
          {
            text: `Cronograma de pagos - ${
              this.credit.value?.idcredito || "unknown"
            } - ${
              this.userinfo?.esnatural === "1"
                ? this.toolsService.capitalize(
                    `${this.userinfo?.nom || ""} ${this.userinfo?.ap || ""} ${
                      this.userinfo?.am || ""
                    }`.trim()
                  )
                : this.toolsService.capitalize(this.userinfo?.razonsocial || "")
            }`,
            fontSize: 9, // Tamaño más pequeño de la letra
            alignment: "center", // Centrar el texto
            margin: [0, 0, 0, 10], // Margen inferior (20)
          },
        ],
        styles: {
          header: {
            fontSize: 7,
            bold: true,
            margin: [0, 10, 0, 5],
          },
          subheader: {
            fontSize: 7,
            bold: false,
            margin: [0, 5, 0, 5],
          },
          tableHeader: {
            bold: true,
            fontSize: 7,
            color: "black",
            fillColor: "#f2f2f2",
          },
          tableCell: {
            fontSize: 7,
            color: "black",
          },
        },
      };

      // Define table headers
      const tableHeaders = [
        { text: "Num. Cuota", style: "tableHeader" },
        { text: "Fecha Cuota", style: "tableHeader" },
        { text: "Interés", style: "tableHeader" },
        { text: "Capital", style: "tableHeader" },
        { text: "Aporte", style: "tableHeader" },
        { text: "Seguro", style: "tableHeader" },
        { text: "Cuota", style: "tableHeader" },
        { text: "Saldo", style: "tableHeader" },
        { text: "Cancelado", style: "tableHeader" },
      ];

      // Map schedule rows
      const tableRows =
        this.schedules.value?.map((schedule) => [
          { text: schedule.numcuota, style: "tableCell" },
          { text: this.formatDate(schedule.fechacuota), style: "tableCell" },
          {
            text: `${this.coin.value} ${this.getFloat(schedule.interes)}`,
            style: "tableCell",
          },
          {
            text: `${this.coin.value} ${this.getFloat(schedule.capital)}`,
            style: "tableCell",
          },
          {
            text: `${this.coin.value} ${this.getFloat(schedule.aporte)}`,
            style: "tableCell",
          },
          {
            text: `${this.coin.value} ${this.getFloat(schedule.seguro)}`,
            style: "tableCell",
          },
          {
            text: `${this.coin.value} ${this.getFloat(schedule.cuota)}`,
            style: "tableCell",
          },
          {
            text: `${this.coin.value} ${this.getFloat(schedule.saldo)}`,
            style: "tableCell",
          },
          { text: this.isCanceled(schedule.es_cancelado), style: "tableCell" },
        ]) || [];

      if (Array.isArray(docDefinition.content)) {
        docDefinition.content.push({
          table: {
            headerRows: 1,
            widths: ["auto", "*", "*", "*", "*", "*", "*", "*", "*"],
            body: [tableHeaders, ...tableRows],
          },
          layout: "lightHorizontalLines",
        });
      }

      // Generate PDF
      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      const blob: Blob = await new Promise((resolve, reject) => {
        pdfDocGenerator.getBlob((blob) => {
          resolve(blob);
        });
      });

      const timestamp = new Date().getTime();
      const fileName = `CP_${
        this.credit.value?.idcredito || "unknown"
      }_${timestamp}.pdf`;

      // Download PDF in browser
      const pdfUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      this.loading_pdf.value = false;
      this.toast.showToast(
        "Su cronograma ha asido descargado",
        ToastType.Success
      );
      this.closeDialog();
    } catch (e) {
      console.log(e);
      this.toast.showToast(
        "Ups algo salio mal, no hemos podido generar su cronograma en PDF.",
        ToastType.Error
      );
    }
  };

  public openDialog(_credit: Credit): void {
    this.credit.value = _credit;
    this.schedules.value = _credit.cronogramas;
    this.dialog.value = true;
  }

  public closeDialog(): void {
    this.schedules.value = [];
    this.dialog.value = false;
  }
}
